// H-1 添削結果の整形表示コンポーネント。
// - Markdown を描画(react-markdown + remark-gfm)
// - 重大度 A(法令=赤)/ B(規約=琥珀)/ C(効果=青)をバッジ・左罫線で視覚化
// - 「生成→人が編集→確定」のため、プレビュー / 編集(textarea)をトグル
// - 診断モード: ## セクションをアコーディオン化
// - 改善モード(## 改善版原稿 を含む): After(完成原稿)を主役カード化+プレーンコピー、
//   Before は参考折りたたみ、変更理由・要クライアント確認フラグをセクション化
import { Children, isValidElement, useMemo, useState, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

type Severity = 'A' | 'B' | 'C';

const SEVERITY: Record<Severity, { badge: string; border: string; chip: string; label: string }> = {
  A: { badge: 'bg-rank-1 text-white', border: 'border-rank-1', chip: 'bg-rank-1/10 text-rank-1 border-rank-1', label: '法令' },
  B: { badge: 'bg-rank-2 text-white', border: 'border-rank-2', chip: 'bg-rank-2/10 text-rank-2 border-rank-2', label: '規約' },
  C: { badge: 'bg-brand-accent text-white', border: 'border-brand-accent', chip: 'bg-brand-accent/10 text-brand-accent border-brand-accent', label: '効果' },
};

/** 文字列から重大度タグ([A]/重大度A など)を検出 */
function detectSeverity(text: string): Severity | null {
  if (/\[A\]|重大度\s*A|^A\s*[\(（]|（A）/.test(text)) return 'A';
  if (/\[B\]|重大度\s*B|^B\s*[\(（]|（B）/.test(text)) return 'B';
  if (/\[C\]|重大度\s*C|^C\s*[\(（]|（C）/.test(text)) return 'C';
  return null;
}

/** React children を素のテキストに平坦化(見出しの重大度検出用) */
function flattenText(children: ReactNode): string {
  let out = '';
  Children.forEach(children, (c) => {
    if (typeof c === 'string' || typeof c === 'number') out += String(c);
    else if (isValidElement(c)) out += flattenText((c.props as { children?: ReactNode }).children);
  });
  return out;
}

/** 判定マーク(◎○△✕)を色付け */
function colorizeMarks(text: string): ReactNode[] {
  return text.split(/(◎|○|△|✕|✗|×|✓)/).map((part, i) => {
    if (/[◎○✓]/.test(part)) return <span key={i} className="font-bold text-rank-5">{part}</span>;
    if (/[△]/.test(part)) return <span key={i} className="font-bold text-rank-2">{part}</span>;
    if (/[✕✗×]/.test(part)) return <span key={i} className="font-bold text-rank-1">{part}</span>;
    return <span key={i}>{part}</span>;
  });
}

/** children 内の文字列ノードだけ判定マークを色付け */
function transform(children: ReactNode): ReactNode {
  return Children.map(children, (c) =>
    typeof c === 'string' ? <>{colorizeMarks(c)}</> : c,
  );
}

const mdComponents = {
  h2: ({ children }: { children?: ReactNode }) => (
    <h4 className="mt-4 mb-2 text-base font-bold text-slate-800">{transform(children)}</h4>
  ),
  h3: ({ children }: { children?: ReactNode }) => {
    const sev = detectSeverity(flattenText(children));
    if (sev) {
      const s = SEVERITY[sev];
      return (
        <h5 className={`mt-4 mb-1.5 flex items-center gap-2 border-l-4 pl-2 text-sm font-bold text-slate-800 ${s.border}`}>
          <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[11px] font-bold ${s.badge}`}>
            {sev}・{s.label}
          </span>
          <span>{transform(children)}</span>
        </h5>
      );
    }
    return <h5 className="mt-4 mb-1.5 text-sm font-bold text-slate-700">{transform(children)}</h5>;
  },
  h4: ({ children }: { children?: ReactNode }) => (
    <h6 className="mt-3 mb-1 text-sm font-semibold text-slate-700">{transform(children)}</h6>
  ),
  p: ({ children }: { children?: ReactNode }) => (
    <p className="my-1.5 text-sm leading-relaxed text-slate-700">{transform(children)}</p>
  ),
  ul: ({ children }: { children?: ReactNode }) => (
    <ul className="my-1.5 ml-4 list-disc space-y-1 text-sm leading-relaxed text-slate-700 marker:text-slate-400">{children}</ul>
  ),
  ol: ({ children }: { children?: ReactNode }) => (
    <ol className="my-1.5 ml-4 list-decimal space-y-1.5 text-sm leading-relaxed text-slate-700 marker:text-slate-400 marker:font-semibold">{children}</ol>
  ),
  li: ({ children }: { children?: ReactNode }) => <li className="pl-0.5">{transform(children)}</li>,
  strong: ({ children }: { children?: ReactNode }) => (
    <strong className="font-semibold text-slate-900">{transform(children)}</strong>
  ),
  hr: () => <hr className="my-3 border-slate-200" />,
  table: ({ children }: { children?: ReactNode }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  th: ({ children }: { children?: ReactNode }) => (
    <th className="border border-slate-200 bg-slate-50 px-2 py-1 text-left font-semibold">{children}</th>
  ),
  td: ({ children }: { children?: ReactNode }) => (
    <td className="border border-slate-200 px-2 py-1 align-top">{transform(children)}</td>
  ),
};

function Markdown({ children }: { children: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
      {children}
    </ReactMarkdown>
  );
}

/** 重大度別件数の抽出(例:「A (法令適合): 6件」) */
function parseCounts(md: string): Record<Severity, number | null> {
  const find = (letter: Severity): number | null => {
    const m = md.match(new RegExp(`${letter}[^\\n0-9]*?(\\d+)\\s*件`));
    return m ? Number(m[1]) : null;
  };
  return { A: find('A'), B: find('B'), C: find('C') };
}

interface Section {
  title: string;
  body: string;
}

/**
 * Markdown を見出し(## または ###)で分割。
 * コードフェンス(``` ... ```)の内側にある見出し記号は分割対象にしない。
 * (改善モードの After 原稿はフェンス内に `## 募集要項` 等を含むため必須)
 */
function splitByHeading(md: string, hashes: '##' | '###'): Section[] {
  const headingRe = new RegExp(`^${hashes}\\s+(.+?)\\s*$`);
  const lines = md.split('\n');
  const sections: Section[] = [];
  let current: Section | null = null;
  const preamble: string[] = [];
  let inFence = false;
  for (const line of lines) {
    if (/^\s*```/.test(line)) inFence = !inFence;
    const m = !inFence ? line.match(headingRe) : null;
    if (m) {
      if (current) sections.push(current);
      current = { title: m[1], body: '' };
    } else if (current) {
      current.body += line + '\n';
    } else {
      preamble.push(line);
    }
  }
  if (current) sections.push(current);
  const pre = preamble.join('\n').trim();
  if (pre) sections.unshift({ title: '', body: pre });
  return sections;
}

/** 最初のコードフェンス内のテキストを取り出す(無ければ trim した本文) */
function extractFence(text: string): string {
  const m = text.match(/```[a-zA-Z]*\n([\s\S]*?)```/);
  return m ? m[1].replace(/\s+$/, '') : text.trim();
}

/** 箇条書き行を配列化(チェックリスト用) */
function parseBullets(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.match(/^\s*(?:[*-]|\d+\.)\s+(.*)$/))
    .filter((m): m is RegExpMatchArray => !!m)
    .map((m) => m[1].replace(/\*\*/g, '').trim())
    .filter(Boolean);
}

/** コピー用に Markdown 記号を軽く除去(媒体貼り付け・クライアント送付向け) */
function stripMarkdown(md: string): string {
  return md
    .replace(/```[a-zA-Z]*\n?/g, '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/^\s*[*-]\s+/gm, '・')
    .replace(/^\s*(\d+)\.\s+/gm, '$1. ')
    .replace(/^---+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function CopyButton({
  text,
  label = 'コピー',
  done = '✓ コピーしました',
  variant = 'secondary',
}: {
  text: string;
  label?: string;
  done?: string;
  variant?: 'secondary' | 'primary';
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* クリップボード不可環境は無視 */
    }
  };
  const base = variant === 'primary' ? 'btn-primary' : 'btn-secondary';
  return (
    <button onClick={copy} className={`${base} !px-3 !py-1 text-xs`}>
      {copied ? done : label}
    </button>
  );
}

function CollapsibleSection({
  title,
  body,
  defaultOpen,
  headerExtra,
}: {
  title: string;
  body: string;
  defaultOpen: boolean;
  headerExtra?: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 bg-slate-50 px-4 py-2.5 text-left hover:bg-slate-100"
      >
        <span className="text-sm font-bold text-slate-800">{title}</span>
        <span className="flex items-center gap-2">
          {open && headerExtra && <span onClick={(e) => e.stopPropagation()}>{headerExtra}</span>}
          <span className={`text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
        </span>
      </button>
      {open && (
        <div className="px-4 py-3">
          <Markdown>{body}</Markdown>
        </div>
      )}
    </div>
  );
}

function SeverityChips({ counts }: { counts: Record<Severity, number | null> }) {
  const order: Severity[] = ['A', 'B', 'C'];
  const any = order.some((s) => counts[s] !== null);
  if (!any) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {order.map((s) => {
        if (counts[s] === null) return null;
        const conf = SEVERITY[s];
        const isA = s === 'A';
        const hit = (counts[s] ?? 0) > 0;
        return (
          <span
            key={s}
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 font-semibold ${conf.chip} ${
              isA ? 'py-1.5 text-sm' : 'py-1 text-xs'
            } ${isA && hit ? 'ring-2 ring-rank-1/30' : ''}`}
          >
            <span className={`inline-flex items-center rounded px-1.5 text-[11px] font-bold ${conf.badge}`}>{s}</span>
            {conf.label} {counts[s]}件
          </span>
        );
      })}
    </div>
  );
}

/** 改善モードの「## 改善版原稿」セクション(Before/After/変更理由/要確認)を専用表示 */
function RewriteDraftSection({ body }: { body: string }) {
  const subs = splitByHeading(body, '###');
  const find = (re: RegExp) => subs.find((s) => re.test(s.title));
  const after = find(/After|改善後|改善版/i);
  const before = find(/Before|元(の)?原稿|改善前/i);
  const reasons = find(/変更理由|改善理由|修正(理由|点)/);
  const flags = find(/要(クライアント)?確認|クライアント確認/);
  const afterText = after ? extractFence(after.body) : '';
  const beforeText = before ? extractFence(before.body) : '';
  const flagItems = flags ? parseBullets(flags.body) : [];

  const [beforeOpen, setBeforeOpen] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);

  return (
    <div className="space-y-3">
      {/* After = 主役の完成原稿 */}
      {afterText && (
        <div className="overflow-hidden rounded-lg border-2 border-brand bg-white shadow-sm">
          <div className="flex items-center justify-between gap-2 bg-brand px-4 py-2.5 text-white">
            <span className="flex items-center gap-2 text-sm font-bold">
              <span aria-hidden>★</span> 改善後の完成原稿(After)
            </span>
            <CopyButton
              text={stripMarkdown(afterText)}
              label="この原稿をコピー(媒体貼り付け用)"
              done="✓ コピーしました"
            />
          </div>
          <div className="px-4 py-3">
            <p className="mb-2 text-xs text-brand">
              ↓ これが完成原稿です。「コピー」で装飾を除いたプレーンな形がコピーされ、そのまま媒体の入力欄に貼れます。
            </p>
            <div className="rounded-md bg-slate-50 px-4 py-3">
              <Markdown>{afterText}</Markdown>
            </div>
          </div>
        </div>
      )}

      {/* 要クライアント確認フラグ = チェックリスト */}
      {flagItems.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-rank-2 bg-white">
          <div className="bg-rank-2/10 px-4 py-2.5 text-sm font-bold text-rank-2">
            ✔ 要クライアント確認フラグ(送付前に確認する項目)
          </div>
          <ul className="space-y-1.5 px-4 py-3">
            {flagItems.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="mt-0.5 shrink-0 text-rank-2" aria-hidden>☐</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 変更理由 = 折りたたみ */}
      {reasons && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <button
            type="button"
            onClick={() => setReasonOpen((o) => !o)}
            className="flex w-full items-center justify-between bg-slate-50 px-4 py-2.5 text-left hover:bg-slate-100"
          >
            <span className="text-sm font-bold text-slate-800">変更理由(なぜ変えたか)</span>
            <span className={`text-slate-400 transition-transform ${reasonOpen ? 'rotate-90' : ''}`}>▶</span>
          </button>
          {reasonOpen && (
            <div className="px-4 py-3">
              <Markdown>{reasons.body}</Markdown>
            </div>
          )}
        </div>
      )}

      {/* Before = 参考(折りたたみ、既定で閉) */}
      {beforeText && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <button
            type="button"
            onClick={() => setBeforeOpen((o) => !o)}
            className="flex w-full items-center justify-between bg-slate-50 px-4 py-2.5 text-left hover:bg-slate-100"
          >
            <span className="text-sm font-medium text-slate-500">元の原稿(Before・参考)</span>
            <span className={`text-slate-400 transition-transform ${beforeOpen ? 'rotate-90' : ''}`}>▶</span>
          </button>
          {beforeOpen && (
            <div className="px-4 py-3">
              <pre className="whitespace-pre-wrap rounded-md bg-slate-50 px-3 py-2 font-mono text-xs leading-relaxed text-slate-500">
                {beforeText}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AdReviewResult({
  markdown,
  onChange,
}: {
  markdown: string;
  onChange: (v: string) => void;
}) {
  const [mode, setMode] = useState<'preview' | 'edit'>('preview');
  const sections = useMemo(() => splitByHeading(markdown, '##'), [markdown]);
  const counts = useMemo(() => parseCounts(markdown), [markdown]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="inline-flex rounded-md border border-slate-300 p-0.5">
          {(['preview', 'edit'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`rounded px-3 py-1 text-xs font-medium transition ${
                mode === m ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {m === 'preview' ? '整形プレビュー' : '編集'}
            </button>
          ))}
        </div>
        <CopyButton text={markdown} label="全体をコピー(Markdown)" />
      </div>

      {mode === 'edit' ? (
        <div className="space-y-2">
          <textarea
            className="input min-h-[600px] resize-y whitespace-pre-wrap font-mono text-xs leading-relaxed"
            value={markdown}
            onChange={(e) => onChange(e.target.value)}
            aria-label="添削結果(編集)"
          />
          <div className="flex justify-end">
            <button onClick={() => setMode('preview')} className="btn-primary !py-1.5 text-xs">
              編集を確定してプレビューに戻る
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <SeverityChips counts={counts} />
          {sections.map((sec, i) => {
            if (sec.title === '') {
              return (
                <div key={i} className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                  <Markdown>{sec.body}</Markdown>
                </div>
              );
            }
            // 改善モードの完成原稿セクションは専用表示
            if (/改善版原稿|改善案|リライト/.test(sec.title)) {
              return (
                <div key={i} className="space-y-2">
                  <h3 className="px-1 text-sm font-bold text-brand">{sec.title}</h3>
                  <RewriteDraftSection body={sec.body} />
                </div>
              );
            }
            const isClientSummary = /クライアント報告用/.test(sec.title);
            return (
              <CollapsibleSection
                key={i}
                title={sec.title}
                body={sec.body}
                defaultOpen={!/軸別診断/.test(sec.title)}
                headerExtra={
                  isClientSummary ? (
                    <CopyButton text={stripMarkdown(sec.body)} label="このサマリーをコピー" />
                  ) : undefined
                }
              />
            );
          })}
        </div>
      )}
      <p className="text-xs text-slate-400">
        ※ 生成結果はそのまま確定せず、担当者が内容を確認・編集してから使用してください。
      </p>
    </div>
  );
}
